/**
 * Badge.jsx - Status badge component.
 *
 * Maps common status strings to color variants automatically.
 * You can also pass an explicit variant to override the auto-detection.
 *
 * Props:
 *   text     {string}  - The label to display inside the badge.
 *   variant  {string}  - "good" | "warn" | "bad" | "info" | "accent" | undefined.
 *                        When omitted, the variant is inferred from `text`.
 *
 * Usage:
 *   <Badge text="Active" />           -> green
 *   <Badge text="Pending" />          -> yellow
 *   <Badge text="Blacklisted" />      -> red
 *   <Badge text="Custom" variant="info" /> -> blue
 */

/* ------------------------------------------------------------------ */
/* Status -> variant mapping                                           */
/* ------------------------------------------------------------------ */

const STATUS_VARIANT = {
  // Good (green)
  active: "good",
  approved: "good",
  completed: "good",
  delivered: "good",
  received: "good",
  passed: "good",
  compliant: "good",
  verified: "good",
  enabled: "good",
  "in stock": "good",

  // Warn (yellow)
  pending: "warn",
  "on hold": "warn",
  "in progress": "warn",
  pending_approval: "warn",
  review: "warn",
  "under review": "warn",
  draft: "warn",
  partial: "warn",
  expiring: "warn",

  // Bad (red)
  blacklisted: "bad",
  rejected: "bad",
  inactive: "bad",
  disabled: "bad",
  failed: "bad",
  overdue: "bad",
  cancelled: "bad",
  canceled: "bad",
  expired: "bad",
  critical: "bad",
  deleted: "bad",
  blocked: "bad",
  "out of stock": "bad",

  // Info (blue)
  new: "info",
  submitted: "info",
  scheduled: "info",
  ordered: "info",
};

function resolveVariant(text, variant) {
  if (variant) return variant;
  if (!text) return undefined;
  const key = String(text).toLowerCase().replace(/_/g, " ").trim();
  return STATUS_VARIANT[key];
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function Badge({ text, variant }) {
  const resolved = resolveVariant(text, variant);
  const className = resolved ? `badge ${resolved}` : "badge";

  return <span className={className}>{text ?? "\u2014"}</span>;
}
